import { useMemo } from 'react';
import { parseProductIdentification } from '@/lib/recallUtils';

interface RecallItem {
  product_name: string;
  brand: string;
  gtin?: string | null;
  batch_lot?: string | null;
}

export type SuggestionType = 'product' | 'brand' | 'barcode';

// Normalise les accents pour la recherche
const normalize = (str: string): string =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Détecte si le terme est un code-barres (4+ chiffres consécutifs)
const isBarcode = (term: string): boolean => /^\d{4,}$/.test(term.trim());

export const useSearchSuggestions = (recalls: RecallItem[], searchTerm: string) => {
  return useMemo(() => {
    if (!searchTerm || searchTerm.length < 1) return [];
    
    const termTrimmed = searchTerm.trim();
    const termNormalized = normalize(termTrimmed);
    const startsWithMatches: Array<{ text: string; type: SuggestionType; priority: number; gtin?: string }> = [];
    const containsMatches: Array<{ text: string; type: SuggestionType; priority: number; gtin?: string }> = [];
    const seen = new Set<string>();
    
    // Mode recherche code-barres
    if (isBarcode(termTrimmed)) {
      for (const recall of recalls) {
        if (seen.has(recall.product_name)) continue;
        
        // Priority 1: Check gtin field directly
        if (recall.gtin) {
          const gtinValues = recall.gtin.split(/\s+/);
          for (const code of gtinValues) {
            const cleanCode = code.trim();
            if (!cleanCode) continue;
            
            if (cleanCode === termTrimmed) {
              seen.add(recall.product_name);
              startsWithMatches.push({ 
                text: recall.product_name, 
                type: 'barcode', 
                priority: 0,
                gtin: cleanCode
              });
              break;
            } else if (cleanCode.startsWith(termTrimmed)) {
              seen.add(recall.product_name);
              startsWithMatches.push({ 
                text: recall.product_name, 
                type: 'barcode', 
                priority: 1,
                gtin: cleanCode
              });
              break;
            }
          }
        }
        
        // Priority 2: Extract GTINs from batch_lot using existing parser
        if (!seen.has(recall.product_name) && recall.batch_lot) {
          const parsedBatches = parseProductIdentification(recall.batch_lot);
          for (const batch of parsedBatches) {
            if (batch.gtin) {
              const cleanCode = batch.gtin.trim();
              if (cleanCode === termTrimmed) {
                seen.add(recall.product_name);
                startsWithMatches.push({ 
                  text: recall.product_name, 
                  type: 'barcode', 
                  priority: 0,
                  gtin: cleanCode
                });
                break;
              } else if (cleanCode.startsWith(termTrimmed)) {
                seen.add(recall.product_name);
                startsWithMatches.push({ 
                  text: recall.product_name, 
                  type: 'barcode', 
                  priority: 1,
                  gtin: cleanCode
                });
                break;
              }
            }
          }
        }
        
        if (seen.size >= 6) break;
      }
    } else {
      // Mode recherche texte classique (produit + marque)
      for (const recall of recalls) {
        // Suggérer noms de produits (priorité haute)
        if (recall.product_name && !seen.has(recall.product_name)) {
          const productNormalized = normalize(recall.product_name);
          if (productNormalized.startsWith(termNormalized)) {
            seen.add(recall.product_name);
            startsWithMatches.push({ text: recall.product_name, type: 'product', priority: 1 });
          } else if (productNormalized.includes(termNormalized)) {
            seen.add(recall.product_name);
            containsMatches.push({ text: recall.product_name, type: 'product', priority: 2 });
          }
        }
        
        // Suggérer marques
        if (recall.brand && !seen.has(recall.brand)) {
          const brandNormalized = normalize(recall.brand);
          if (brandNormalized.startsWith(termNormalized)) {
            seen.add(recall.brand);
            startsWithMatches.push({ text: recall.brand, type: 'brand', priority: 3 });
          } else if (brandNormalized.includes(termNormalized)) {
            seen.add(recall.brand);
            containsMatches.push({ text: recall.brand, type: 'brand', priority: 4 });
          }
        }
        
        // Limiter pour performance
        if (seen.size >= 12) break;
      }
    }
    
    // Combiner et trier : startsWith d'abord, puis contains
    return [...startsWithMatches, ...containsMatches]
      .sort((a, b) => a.priority - b.priority)
      .map(({ text, type, gtin }) => ({ text, type, gtin }))
      .slice(0, 6);
  }, [recalls, searchTerm]);
};
